import { ReactElement, useCallback, useEffect, useRef, useState } from 'react'
import { ICartProductData } from '../../../../../cart/src'
import styled from 'styled-components'
import { breakpoints } from '../../../../../../src/theme/breakpoints'
import { QuantitySelector } from '../../../../../../src/ui-components/products/QuantitySelector'
import { observer } from 'mobx-react-lite'
import { LinearProgress } from '@mui/material'
import { useQuantitySelectorToolbox } from '../../../../../../src/ui-components/products/useQuantitySelectorToolbox'
import { useStockListLabel } from '../../../../../../src/ui-components/products/useStockListLabel'

interface Props {
  cartProduct: ICartProductData
  qty: number
  setQty: (qty: number) => void
  orderIfNotAvailable: boolean
}

const QuantitySelectorWrapper = observer((props: Props): ReactElement => {
  const { cartProduct, qty, setQty, orderIfNotAvailable } = props
  const [inputValue, setInputValue] = useState(qty.toString())

  // sync when parent qty changes
  useEffect(() => {
    setInputValue(qty.toString())
  }, [qty])

  const {
    updateQuantity,
    priceSpinner,
    setPriceSpinner,
    activeLocation: location,
    product,
  } = cartProduct

  const stockListLabel = useStockListLabel(
    Boolean(product?.isStocked),
    product?.stockedLists
  )

  const updateQuantityRef = useRef(updateQuantity)
  const locationRef = useRef(location)
  updateQuantityRef.current = updateQuantity
  locationRef.current = location

  const { onInputTooltipMsg, shouldShowLocationSelector } =
    useQuantitySelectorToolbox(cartProduct)

  const isPrimary = location?.locType === 'primary'

  const handleQtyChange = useCallback(
    (newQty: number): Promise<void> => {
      setQty(newQty)
      setPriceSpinner?.(true)
      return updateQuantityRef
        .current(newQty, locationRef.current)
        .then(() => undefined)
    },
    [setQty, setPriceSpinner]
  )

  return (
    <Wrapper>
      <QuantitySelector
        orderIfNotAvailable={orderIfNotAvailable && isPrimary}
        onClick={shouldShowLocationSelector}
        onRemove={() => undefined}
        minQty={Math.max(location?.minQty ?? 0, location?.buyIncrement || 0)}
        stepSize={location?.buyIncrement}
        onInputHoverTooltipMsg={onInputTooltipMsg}
        stockListTooltipMsg={stockListLabel}
        isStoked={product?.isStocked}
        isCondensedView={false}
        isLoading={priceSpinner}
        value={inputValue}
        setValue={(val) => setInputValue(val)} // local string state
        onChange={handleQtyChange}
      />
      {priceSpinner && <LinearProgress />}
      <MinQtyDiplayer>
        <span>{`Per Car Qty: ${
          location?.minQty ? location.minQty : '1'
        }`}</span>
      </MinQtyDiplayer>
    </Wrapper>
  )
})

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 168px;
`

const MinQtyDiplayer = styled.div`
  display: none;
  @media screen and (max-width: ${breakpoints.mobileLarge}px) {
    display: flex;
    flex-direction: row;
    color: #6b6b6b;
    font-size: 14px;
  }
`

export default QuantitySelectorWrapper
